import React, { useEffect, useState, ChangeEvent } from "react";
import { Container, RadioButtonWithLabel } from "../../../components";
import { Heading, Button } from "@telia/teddy";
import { useSelector } from "react-redux";
import AcceptContent from "./AcceptContent";
import InformationBlocks from "./InformationBlocks";
import RejectContent from "./RejectContent";
import { RootState } from "../../../../../models";
import { RejectReason, RejectReasonID } from "../../../../../models/transfer";
import { isPortin as isPortinSelector } from "../../../app/state/selectors";
import { isBankIdVerified as isBankIdVerifiedSelector } from "../../../authentication/state/selectors";
import {
    getOtpVerifiedPhoneNumber,
    getSelectedFormerLegalOrganization,
    getTransferExistingOrganizationName,
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
} from "../../../checkout/state/selectors/accountSelectors";
import TEXT from "../../text-consts";

type SelectedOption = "accept" | "reject";
const SELECTED_OPTION_STORAGE_KEY = "consentFormerLegal.selectedOption";

// The page is reloaded after BankID, so the chosen option must survive the redirect
const readStoredSelectedOption = (): SelectedOption | null => {
    try {
        const value = window.sessionStorage.getItem(SELECTED_OPTION_STORAGE_KEY);
        return value === "accept" || value === "reject" ? value : null;
    } catch {
        return null;
    }
};

const storeSelectedOption = (value: SelectedOption) => {
    try {
        window.sessionStorage.setItem(SELECTED_OPTION_STORAGE_KEY, value);
    } catch {
        // sessionStorage may be unavailable (e.g. private mode) - the default option will then be used after BankID
    }
};

interface Props {
    breakageFee: number;
    isB2BTransfer: boolean;
    rejectAction: (reasonText: string, reasonId: string) => unknown;
    submitAction: () => unknown;
}

const ApprovalChoices = ({ breakageFee, isB2BTransfer, submitAction, rejectAction }: Props) => {
    const isBankIdVerified = useSelector((state: RootState) => isBankIdVerifiedSelector(state));
    const selectedFormerLegalOrganization = useSelector((state: RootState) =>
        getSelectedFormerLegalOrganization(state),
    );
    const otpVerifiedPhoneNumber = useSelector((state: RootState) => getOtpVerifiedPhoneNumber(state));
    const isExistingLegalOwnerVerified = useSelector((state: RootState) => isExistingLegalOwnerVerifiedSelector(state));
    const isPortinOrder = useSelector((state: RootState) => isPortinSelector(state));
    const formerLegalOrganizationName = useSelector((state: RootState) => getTransferExistingOrganizationName(state));
    // after BankID (page reload) or one-time code, continue where the verification was left
    const isVerificationStarted = isBankIdVerified || !!otpVerifiedPhoneNumber;
    const isOrganizationConsent = !!selectedFormerLegalOrganization || !!formerLegalOrganizationName;
    const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(
        isVerificationStarted ? "accept" : null,
    );
    const [subSelectedOption, setSubSelectedOption] = useState<"accept-b2c" | "accept-b2b" | null>(() => {
        if (!isVerificationStarted) return null;
        return isOrganizationConsent ? "accept-b2b" : "accept-b2c";
    });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [hasClickedRejected, setHasClickedRejected] = useState(false);
    const [rejectReasons, setRejectReasons] = useState<RejectReasonID | null>(null);
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectReasonInput, setRejectReasonInput] = useState(false);
    const isFormerLegalVerified = isExistingLegalOwnerVerified;
    // For portin the one giving consent chooses, for transfer it is given by who owns the subscription today
    const getDefaultSubOption = () => {
        if (isPortinOrder) return null;
        return formerLegalOrganizationName ? "accept-b2b" : "accept-b2c";
    };

    useEffect(() => {
        const storedOption = readStoredSelectedOption();
        if (storedOption && (isVerificationStarted || isExistingLegalOwnerVerified)) {
            setSelectedOption(storedOption);
        }
        // only restore the option on first render after a reload
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectOption = (option: SelectedOption) => {
        setSelectedOption(option);
        storeSelectedOption(option);
        setRejectReasons(null);
        setSubSelectedOption(getDefaultSubOption());
    };
    const reasons: RejectReason[] = [
        { id: "NOT_OWNER", text: "Jeg er ikke eier av dette nummeret" },
        { id: "NOT_FAMILIAR_NUMBER", text: "Jeg kjenner ikke til at dette nummeret skal overføres" },
        { id: "OTHER", text: "Annet", reason: rejectReason },
    ];

    if (breakageFee) {
        reasons.splice(2, 0, { id: "NOT_ACCEPTABLE_FEE", text: "Jeg aksepterer ikke bruddgebyret" });
    }

    const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        setRejectReason((e.target as HTMLInputElement).value);
    };

    const handleSubmitTransferOwnershipOrder = () => {
        setHasClickedSubmit(true);
        Promise.resolve(submitAction()).catch(() => setHasClickedSubmit(false));
    };

    const handleRejectReasons = (id: RejectReasonID) => {
        if (id === "OTHER") {
            setRejectReasonInput(!showRejectReasonInput);
        }
        setRejectReasons(id);
    };

    const handleRejectTransferOwnershipOrder = () => {
        setHasClickedRejected(true);
        const reason = reasons.find((r) => r.id === rejectReasons);

        if (rejectReasons && reason) {
            let reasonText = "";
            let reasonId = "OTHER";

            if (rejectReasons === "OTHER" && rejectReason) {
                reasonText = rejectReason;
            } else {
                reasonText = reason.text;
                reasonId = rejectReasons;
            }

            Promise.resolve(rejectAction(reasonText, reasonId)).catch(() => setHasClickedRejected(false));
        }
    };

    const submitButtonText = {
        B2B: TEXT.CONSENT_FORMER_LEGAL.BUTTON.NEXT_STEP,
        B2C: TEXT.CONSENT_FORMER_LEGAL.BUTTON.CONFIRM,
    }[isB2BTransfer ? "B2B" : "B2C"];

    return (
        <>
            <Container size="medium" whiteBg className="consent-landing-page__section">
                <div className="consent-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="accept"
                        onChange={() => handleSelectOption("accept")}
                        name="consentB2B"
                        checked={selectedOption === "accept"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="consent-landing-page__consent-radio-heading"
                        >
                            {"Jeg godkjenner overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {selectedOption === "accept" && (
                        <AcceptContent
                            isB2BTransfer={isB2BTransfer}
                            subSelectedOption={subSelectedOption}
                            setSubSelectedOption={setSubSelectedOption}
                            setRejectReasons={setRejectReasons}
                        />
                    )}
                </div>
                <div className="consent-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="reject"
                        onChange={() => handleSelectOption("reject")}
                        name="consentB2B"
                        checked={selectedOption === "reject"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="consent-landing-page__consent-radio-heading"
                        >
                            {"Jeg avslår overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {selectedOption === "reject" && (
                        <RejectContent
                            handleReasonChange={handleReasonChange}
                            handleRejectReasons={handleRejectReasons}
                            reasons={reasons}
                            rejectReason={rejectReason}
                            rejectReasons={rejectReasons}
                        />
                    )}
                </div>
            </Container>
            {!isB2BTransfer && isExistingLegalOwnerVerified && <InformationBlocks.ExistingOwnerInformation />}
            <Container size="medium" className="consent-landing-page__right-align">
                {selectedOption === "accept" && (isBankIdVerified || isFormerLegalVerified) && (
                    <Button
                        variant="primary"
                        disabled={!isFormerLegalVerified || hasClickedSubmit}
                        onClick={handleSubmitTransferOwnershipOrder}
                    >
                        {submitButtonText}
                    </Button>
                )}
                {selectedOption === "reject" && rejectReasons && (
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={hasClickedRejected}
                        onClick={handleRejectTransferOwnershipOrder}
                    >
                        {TEXT.CONSENT_FORMER_LEGAL.BUTTON.NEXT_STEP}
                    </Button>
                )}
            </Container>
        </>
    );
};

export default ApprovalChoices;
